import { format, sub } from 'date-fns';
import moment from 'moment';

export const numbersFormatSeparator = '.';
export const numbersThousandsFormatSeparator = ',';

/**
 * Do not add new uses of it and consequently be replacing it with `dateFormat`.
 * Replacement should happen in DatePicker components and then form validators
 * containing those pickers.
 * @see {@link dateFormat}
 */
export const dateFormatOld = 'DD-MM-YYYY';

export const dateFormat = 'dd/MM/yyyy';

export const dateFormatSeparator = dateFormatOld.split('').find((item) => !item.match(/^[a-zA-Z]*$/));

export const thousandsSeparator = ' ' as const;

export function formatInteger(number: string, toFixed?: boolean) {
  if (number !== undefined && number !== '') {
    return parseFloat(number)
      .toFixed(toFixed ? 2 : 0)
      .toString()
      .replace(/\./g, '.')
      .replace(/\D,+/g, '')
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${numbersThousandsFormatSeparator}`);
  }
  return undefined;
}

export const formatPrice = (amount: number, minimumFractionDigits = 2) => {
  return Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits,
  }).format(amount);
};

export const formatPower = (val: string) => {
  if (val) {
    const power = new Intl.NumberFormat('de-DE', {
      maximumSignificantDigits: 3,
    })
      .format(parseInt(val, 10))
      .toString();
    return formatInteger(val) ? `${power}${' kW(PS)'}` : '';
  }
  return '';
};

export const formatConsumption = (val: string) => {
  if (val) {
    const consumption = new Intl.NumberFormat('de-DE', {
      maximumSignificantDigits: 3,
    })
      .format(parseInt(val, 10))
      .toString();
    return formatInteger(val) ? `${consumption}${' km/l'}` : '';
  }
  return '';
};

export const formatEngineSize = (val: string) => {
  if (val) {
    const engineSize = new Intl.NumberFormat('de-DE', {
      maximumSignificantDigits: 3,
    })
      .format(parseInt(val, 10))
      .toString();
    return formatInteger(val) ? `${engineSize}${' cc'}` : '';
  }
  return '';
};

export const formatWeight = (val: string) => {
  if (val) {
    const weight = new Intl.NumberFormat('de-DE', {
      maximumSignificantDigits: 3,
    })
      .format(parseInt(val, 10))
      .toString();
    return formatInteger(val) ? `${weight}${' kg'}` : '';
  }
  return '';
};

export const formatSize = (val: string) => {
  if (val) {
    const size = new Intl.NumberFormat('de-DE', {
      maximumSignificantDigits: 3,
    })
      .format(parseInt(val, 10))
      .toString();
    return formatInteger(val) ? `${size}${' mm'}` : '';
  }
  return '';
};

export const normalizeDate = (value) => {
  if (!value) return value;
  // it is already in the proper format
  if (value.indexOf('/') !== -1) return value;

  return moment(new Date(value)).format('DD/MM/YYYY');
};

export const isoDate = (value) => {
  if (!value) return value;
  // it is already in the proper format
  if (value.indexOf('-') !== -1) return value;

  return value.split('/')?.reverse().join('-');
};

export const formatDateWithPoints = (value): string => {
  if (!value) return value;
  // it is already in the proper format
  if (value.indexOf('.') !== -1) return value;

  return moment(new Date(value)).format('DD.MM.YYYY');
};

export const phoneHelperInitial = (phone: string) => {
  return phone ? `+33 ${phone}` : '';
};

export function phoneHelper(phone: any, part: string) {
  if (!phone) return '';
  let number = '';
  try {
    switch (part) {
      case 'code':
        return phone.split(' ')[0];
      case 'phone':
      case 'number':
        // eslint-disable-next-line prefer-destructuring
        number = phone.split(' ')[1];
        if (number.charAt(0) === '0') {
          return number.slice(1);
        }
        return number;
      default:
        break;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
  return '';
}

export const formatDecimalComma = (value: any): null | string => {
  if (!value) return null;
  return value.toString().replace('.', ',').replace(' ', '');
};

export const allowOnlyInt = (value: any): null | number => {
  if (value === '') return null;
  // eslint-disable-next-line radix
  return value === 0 ? value.toString() : parseInt(value);
};
export const formatNumberWithSpace = (number) => {
  // Convert the number to a string
  const numberString = String(number);
  const parts = numberString.includes('.') ? numberString.split('.') : numberString.split(',');

  // Use regular expression to add space every three digits from the end
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  const separator = numberString.includes('.') ? '.' : ',';

  return parts.join(separator);
};

export const formatValidNumberWithDecimalSeparator = (value: any, onlyInt = false): any => {
  if (!value) return null;

  const formattedValue = value
    .toString()
    .replace(/\s/g, '')
    .replace(',', '.')
    .replace(/[^0-9.,]/g, '');

  if (!formattedValue) {
    return '';
  }

  if ((!onlyInt && formattedValue.toString().slice(-1) === ',') || formattedValue.toString().slice(-1) === '.') {
    return formatNumberWithSpace(formattedValue.toString().replace('.', ','));
  }
  return onlyInt
    ? formatNumberWithSpace(allowOnlyInt(formattedValue.toString())).toString().replace('.', ',')
    : formatNumberWithSpace(parseFloat(formattedValue.toString())).toString().replace('.', ',');
};

export const unFormatNumberWithSpaces = (value) => {
  if (!value) return null;
  // eslint-disable-next-line
  if (typeof value === 'number') return value;
  return value.replace(/\s/g, '');
};

export const roundToNearest = (value, roundedTo = 5000): number => {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  return Math.round(value / roundedTo) * roundedTo;
};

export const formatNumberWithSpaceAndDecimal = (number: any) => {
  // Convert the number to a string
  const numberString = formatDecimalComma(String(number)?.replaceAll(thousandsSeparator, '')) || '';

  const hasDecimalPart = numberString.includes(',');

  // Split the string into integer and decimal parts
  const [integerPart, decimalPart] = numberString.split(',');

  // Add space every three digits from the end of the integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

  // Combine the integer and decimal parts with a comma as the decimal separator
  const formattedNumber = hasDecimalPart ? `${formattedInteger},${decimalPart}` : formattedInteger;

  return formattedNumber;
};

export const transformDateFormatWithSlash = (inputDate: string) => {
  const dateParts = inputDate.split('/');
  const transformedDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
  return transformedDate;
};

/**
 * - Pass years, months and/or days in the past.
 */
export const getFormattedDateInPast = ({
  years,
  months,
  days,
}: { years?: number; months?: number; days?: number } = {}) => {
  return format(
    sub(Date.now(), {
      years,
      months,
      days,
    }),
    dateFormat
  );
};

/**
 * Removes all leading zeros from the given mobile phone number.
 *
 * @param mobilePhone - Mobile phone number as string
 * @returns The mobile phone number without leading zeros or `undefined`
 */
export const removeLeadingZerosFromMobilePhone = (mobilePhone?: string): string | undefined => {
  return mobilePhone?.replace(/^0+/, '');
};

/**
 * Normalizes a phone number into a backend-friendly numeric format.
 *
 * What it does:
 * Removes all non-digit characters (spaces, plus sign, dashes, parentheses, etc.)
 * Removes a leading country code 33 (France) when present at the start
 * Removes any remaining leading zeros
 *
 * @param phoneNumber Optional raw phone number entered by the user.
 * @returns A normalized phone number string, or undefined when input is missing.
 * */
export const formatPhoneNumber = (phoneNumber?: string): string | undefined => {
  return phoneNumber?.replace(/\D/g, '')?.replace(/^33/, '')?.replace(/^0+/, '') || undefined;
};
